import type { WsCommand } from '@shared/api/ws/types/ws-command'
import type { WsMessage } from '@shared/api/ws/types/ws-message'
import { IFRAME_MESSAGE_TYPE, IFrameMessageType, sendIframeMessage } from '@shared/iframe-messages'
import { uuid as genUuid } from '@shared/util/uuid'

export interface ProxyWsApi {
  /**
   * Send a raw WebSocket command through the host's WebSocket connection.
   * Fire-and-forget — responses arrive via `on()` event subscriptions.
   */
  send: (command: WsCommand) => void
  /**
   * Send a WebSocket command and wait for the server's response.
   * The command is sent with a `request_id`; the promise resolves when the server echoes it back.
   * Times out after 30 seconds.
   */
  sendWithResponse: (command: WsCommand) => Promise<WsMessage>
  /**
   * Convenience method to send a session command (stop, model, agent, etc.)
   */
  sendCommand: (sessionId: string, data: { command: string, [key: string]: unknown }) => void
}

function waitForWsResult(requestUuid: string, timeoutMs = 30_000): Promise<WsMessage> {
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout>
    const handler = (event: MessageEvent) => {
      if (typeof event.data !== 'string')
        return
      let data: any
      try {
        data = JSON.parse(event.data)
      }
      // eslint-disable-next-line unused-imports/no-unused-vars
      catch (_e) {
        return
      }
      if (
        data.type === IFRAME_MESSAGE_TYPE
        && data.action === IFrameMessageType.OnWsResult
        && data.uuid === requestUuid
      ) {
        clearTimeout(timer)
        window.removeEventListener('message', handler)
        if (data.error)
          reject(new Error(data.error))
        else
          resolve(data.result as WsMessage)
      }
    }
    window.addEventListener('message', handler)
    timer = setTimeout(() => {
      window.removeEventListener('message', handler)
      reject(new Error(`WebSocket request ${requestUuid} timed out after ${timeoutMs}ms`))
    }, timeoutMs)
  })
}

export function createWsAPI(): ProxyWsApi {
  return {
    send(command: WsCommand) {
      sendIframeMessage(IFrameMessageType.CmdWsSend, { command })
    },
    async sendWithResponse(command: WsCommand) {
      const id = genUuid()
      sendIframeMessage(IFrameMessageType.CmdWsSend, { command, uuid: id })
      return waitForWsResult(id)
    },
    sendCommand(sessionId: string, data: { command: string, [key: string]: unknown }) {
      sendIframeMessage(IFrameMessageType.CmdWsSend, {
        command: {
          type: 'session_command',
          session_id: sessionId,
          data,
        },
      })
    },
  }
}
